import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import NuvvyCharacter from '../NuvvyCharacter';
import ConversationHistory from './ConversationHistory';
import DrinkMessageInput from './DrinkMessageInput';
import FinalDrinkCard from './FinalDrinkCard';
import QuickAnswers from './QuickAnswers';
import { parseAgentResponse } from './utils/parseAgentResponse';
import { MEDIA_BASE_URL } from '../../services/indexMediaBaseUrl';

const isEmptyAgentMessageResponse = (response, parsedResponse) => {
  const hasMessage = Boolean(String(response?.message || parsedResponse?.message || '').trim());
  const hasOptions = Array.isArray(response?.options) && response.options.length > 0;
  const hasSuggestions = Array.isArray(parsedResponse?.suggestions) && parsedResponse.suggestions.length > 0;

  return response?.type === 'message'
    && response?.data == null
    && !hasMessage
    && !hasOptions
    && !hasSuggestions;
};

const welcomeMessage = 'أهلًا بيك\nتعالى نعمل مشروب مخصوص على مزاجك.';
const thinkingMessage = 'لحظة... بفكر في أنسب اختيار ليك.';
const finalMessage = 'خلصنا!\nعملتلك مشروب أعتقد إنه مناسب جدًا لذوقك.\nشوفه وقولي رأيك.';
const errorMessage = 'حصلت مشكلة بسيطة، جرب تاني.';

const NuvvyDrinkExperience = ({
  makeDrinkRequest,
  approveDrinkRequest,
  rateDrinkRequest,
  backPath,
  approvedNavigatePath,
  approveSource = 'local_screen',
  title = 'إنشاء مشروبك الخاص',
  subtitle = 'خلّي Nuvvy يساعدك تصمم مشروب على مزاجك',
  enableRating = false,
  onNavigate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const messageInputRef = useRef(null);
  const conversationEndRef = useRef(null);
  const characterTimerRef = useRef(null);

  const [conversationId, setConversationId] = useState(null);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalDecision, setApprovalDecision] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [finalDrinkModalOpen, setFinalDrinkModalOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateValue, setRateValue] = useState(3);
  const [rateComment, setRateComment] = useState('');
  const [rateLoading, setRateLoading] = useState(false);
  const [characterState, setCharacterState] = useState('wave');
  const [characterMessageOverride, setCharacterMessageOverride] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [dessertModalOpen, setDessertModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const parsedAgentResponse = useMemo(
    () => parseAgentResponse(responseData || ''),
    [responseData]
  );

  const isFinalDrinkResponse = responseData?.type === 'drink' || responseData?.type === 'custom_drink';
  const finalDrink = isFinalDrinkResponse ? (responseData?.drink || responseData?.data) : null;
  const isDessertSelection = responseData?.type === 'dessert_selection' || parsedAgentResponse.type === 'dessert_selection';
  const finalDrinkId = responseData?.drink_id || responseData?.data?.drink_id || responseData?.data?.id || null;
  const rawPrice = finalDrink?.price ?? responseData?.price ?? null;
  const finalDrinkPrice = rawPrice !== null && rawPrice !== undefined ? Number(rawPrice) : null;
  const currentQuestion = parsedAgentResponse.message;
  const suggestions = finalDrink || loading || isDessertSelection ? [] : parsedAgentResponse.suggestions;
  const dessertOptions = useMemo(
    () => (isDessertSelection ? parsedAgentResponse.suggestions : []),
    [isDessertSelection, parsedAgentResponse.suggestions]
  );
  const optionNote = parsedAgentResponse.note || (suggestions.length ? 'تقدر تختار من الاختيارات أو تكتب اللي حابه بطريقتك.' : '');
  const isActiveMobileConversation = isMobile && conversationStarted && !finalDrink;
  const splitIndex = Math.ceil(suggestions.length / 2);
  const leftOptions = suggestions.slice(splitIndex);
  const rightOptions = suggestions.slice(0, splitIndex);
  const dessertsByCategory = useMemo(() => (
    dessertOptions.reduce((groups, item) => {
      const category = item.category || 'OTHER';
      return {
        ...groups,
        [category]: [...(groups[category] || []), item],
      };
    }, {})
  ), [dessertOptions]);
  const characterMessage = loading
    ? thinkingMessage
    : finalDrink
      ? finalMessage
      : characterMessageOverride || currentQuestion || welcomeMessage;

  const getCategoryLabel = (category) => {
    const normalizedCategory = String(category || '').toUpperCase();

    if (normalizedCategory === 'BAKERY') return 'مخبوزات';
    if (normalizedCategory === 'DEASERT' || normalizedCategory === 'DESSERT') return 'حلويات';

    return category || 'اختيارات أخرى';
  };

  const resolveDessertImage = (image) => {
    if (!image) return '';

    const imagePath = String(image).trim();
    if (/^https?:\/\//i.test(imagePath)) return imagePath;

    const baseUrl = String(MEDIA_BASE_URL).replace(/\/+$/, '');
    const normalizedPath = imagePath.replace(/^\/+/, '');

    return `${baseUrl}/${encodeURI(normalizedPath)}`;
  };

  const getSelectedDessertFromFinalResponse = () => {
    const dessert = responseData?.dessert;
    const data = responseData?.data || {};

    if (dessert?.id) {
      return {
        id: dessert.id,
        product_id: dessert.id,
        value: dessert.id,
        name: dessert.name,
        label: dessert.name,
        price: dessert.price || 0,
        category: dessert.category || null,
      };
    }

    if (data?.dessert_id) {
      return {
        id: data.dessert_id,
        product_id: data.dessert_id,
        value: data.dessert_id,
        name: data.dessert_name || `Dessert ${data.dessert_id}`,
        label: data.dessert_name || `Dessert ${data.dessert_id}`,
        price: data.dessert_price || 0,
        category: data.dessert_category || null,
      };
    }

    return null;
  };

  const showNotification = (text, severity = 'success') => {
    setNotification({ open: true, message: text, severity });
  };

  const scheduleIdle = () => {
    window.clearTimeout(characterTimerRef.current);
    characterTimerRef.current = window.setTimeout(() => {
      setCharacterState((prev) => (prev === 'talking' ? 'idle' : prev));
    }, 1200);
  };

  const resetConversation = () => {
    window.clearTimeout(characterTimerRef.current);
    setConversationId(null);
    setConversationStarted(false);
    setMessage('');
    setApprovalDecision(null);
    setResponseData(null);
    setConversationHistory([]);
    setFinalDrinkModalOpen(false);
    setDessertModalOpen(false);
    setRateDialogOpen(false);
    setRateValue(3);
    setRateComment('');
    setCharacterState('wave');
    setCharacterMessageOverride('');
    setSelectedOption(null);
  };

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [conversationHistory.length]);

  useEffect(() => () => window.clearTimeout(characterTimerRef.current), []);

  const sendMessage = async (userText = '', displayText = '') => {
    if (loading) return;

    const isFirstMessage = conversationId === null;
    const userMessage = userText.trim();
    const visibleUserMessage = (displayText || userMessage).trim();

    if (!isFirstMessage && !userMessage) {
      showNotification('اكتب إجابتك أو اختر إجابة سريعة', 'warning');
      return;
    }

    setLoading(true);
    setCharacterState('thinking');
    setCharacterMessageOverride('');
    setResponseData(null);
    setSelectedOption(null);

    try {
      let response = await makeDrinkRequest({
        conversation_id: conversationId,
        ...(isFirstMessage ? {} : { message: userMessage }),
      });

      let parsedResponse = parseAgentResponse(response || '');
      const retryConversationId = response?.conversation_id ?? conversationId;

      if (isEmptyAgentMessageResponse(response, parsedResponse)) {
        response = await makeDrinkRequest({
          conversation_id: retryConversationId,
          message: 'again',
        });
        parsedResponse = parseAgentResponse(response || '');
      }

      const nextHistoryItems = [
        ...(!isFirstMessage
          ? [{
              id: `user-${Date.now()}`,
              role: 'user',
              text: visibleUserMessage,
            }]
          : []),
        {
          id: `agent-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          role: 'agent',
          text: parsedResponse.message,
          type: response?.type || null,
        },
      ].filter((entry) => entry.text);

      setConversationStarted(true);
      setConversationId(response?.conversation_id ?? conversationId);
      setConversationHistory((prev) => [...prev, ...nextHistoryItems]);
      setResponseData(response);
      setMessage('');

      if (response?.type === 'drink' || response?.type === 'custom_drink') {
        setCharacterState('happy');
        setFinalDrinkModalOpen(true);
      } else if (response?.type === 'dessert_selection') {
        setCharacterState('talking');
        setDessertModalOpen(true);
      } else {
        setCharacterState('talking');
        scheduleIdle();
      }

      window.setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (error) {
      setCharacterState('idle');
      setCharacterMessageOverride(errorMessage);
      showNotification(error.message || 'حدث خطأ أثناء إنشاء المشروب', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const typedMessage = message.trim();
    const extraText = selectedOption
      ? typedMessage.replace(selectedOption.label, '').trim()
      : '';
    const outgoingMessage = selectedOption
      ? `${selectedOption.value}${extraText ? ` - ${extraText}` : ''}`
      : typedMessage;
    const visibleMessage = typedMessage || selectedOption?.label || '';

    sendMessage(outgoingMessage, visibleMessage);
  };

  const handleMessageChange = (value) => {
    setMessage(value);

    if (selectedOption && !value.includes(selectedOption.label)) {
      setSelectedOption(null);
    }
  };

  const handleQuickAnswerSelect = (value, label) => {
    const nextOption = { value, label };
    setSelectedOption(nextOption);
    setMessage(label);
    window.setTimeout(() => {
      messageInputRef.current?.focus();
    }, 60);
  };

  const handleDessertSelect = async (item) => {
    const selectedDessert = {
      id: item.id || item.value,
      product_id: item.id || item.value,
      value: item.value || item.id,
      name: item.name || item.label,
      label: item.label || item.name,
      price: item.price || 0,
      category: item.category || null,
      image: item.image || null,
    };

    setDessertModalOpen(false);
    sendMessage(String(selectedDessert.value || selectedDessert.id), selectedDessert.label || selectedDessert.name);
  };

  const handleApproveDecision = async (approved) => {
    if (!finalDrinkId) {
      showNotification('لا يوجد drink_id في الاستجابة النهائية لاعتماد أو رفض المشروب', 'error');
      return;
    }

    setApprovalLoading(true);

    try {
      const response = await approveDrinkRequest(finalDrinkId, approved, approveSource);
      setApprovalDecision(approved);

      if (approved === 1 && response) {
        const selectedDessert = getSelectedDessertFromFinalResponse();
        localStorage.setItem('approved_drink', JSON.stringify(response));
        onNavigate(approvedNavigatePath, {
          state: {
            approvedDrink: response,
            ...(selectedDessert ? { selectedDessert } : {}),
          },
        });
      } else if (approved === 0) {
        resetConversation();
      }

      showNotification(
        response?.message || (approved === 1 ? 'تم قبول المشروب' : 'تم رفض المشروب'),
        'success'
      );
    } catch (error) {
      showNotification(error.message || 'حدث خطأ أثناء تحديث حالة المشروب', 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleSubmitRate = async () => {
    if (!finalDrinkId) {
      showNotification('لا يوجد drink_id في الاستجابة النهائية لتقييم المشروب', 'error');
      return;
    }

    if (!rateValue || rateValue < 1 || rateValue > 5) {
      showNotification('الرجاء اختيار تقييم من 1 إلى 5', 'warning');
      return;
    }

    if (!rateDrinkRequest) return;

    setRateLoading(true);

    try {
      const response = await rateDrinkRequest({
        drink_id: finalDrinkId,
        rate: rateValue,
        comment: rateComment,
      });

      setRateDialogOpen(false);
      setRateValue(3);
      setRateComment('');
      showNotification(response?.message || 'تم إرسال تقييم المشروب بنجاح', 'success');
    } catch (error) {
      showNotification(error.message || 'حدث خطأ أثناء إرسال تقييم المشروب', 'error');
    } finally {
      setRateLoading(false);
    }
  };

  const finalDessert = getSelectedDessertFromFinalResponse();
  const finalDessertPrice = finalDessert?.price !== null && finalDessert?.price !== undefined
    ? Number(finalDessert.price)
    : null;
  const finalTotalPrice = (Number.isFinite(finalDrinkPrice) ? finalDrinkPrice : 0)
    + (Number.isFinite(finalDessertPrice) ? finalDessertPrice : 0);

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100dvh',
        background:
          'radial-gradient(circle at 10% 8%, rgba(47, 87, 65, 0.10), transparent 32%), radial-gradient(circle at 88% 85%, rgba(156, 39, 176, 0.08), transparent 30%), linear-gradient(180deg, #f8fbf4 0%, #eef3f8 100%)',
        py: { xs: 0.75, md: 2.5 },
        overflowX: 'hidden',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: isActiveMobileConversation ? 0.25 : 0.75, md: 1 },
          px: { xs: isActiveMobileConversation ? 0.5 : 1, sm: 2, md: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: { xs: isActiveMobileConversation ? 'none' : 'block', sm: 'block' },
            mb: { xs: 1.25, md: 2.5 },
            p: { xs: 1.1, sm: 1.5, md: 2 },
            borderRadius: { xs: 2.25, md: 3 },
            border: '1px solid rgba(47, 87, 65, 0.14)',
            background: 'rgba(255, 255, 255, 0.78)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 16px 38px rgba(23, 53, 37, 0.08)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Box
                sx={{
                  width: { xs: 42, sm: 48 },
                  height: { xs: 42, sm: 48 },
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  background: 'linear-gradient(145deg, #2f5741, #9c27b0)',
                  flexShrink: 0,
                }}
              >
                <AutoFixHighIcon />
              </Box>
              <Box>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 950, color: '#173525', lineHeight: 1.25 }}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>
                  {subtitle}
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <Button
                variant="text"
                startIcon={<RestartAltIcon />}
                onClick={resetConversation}
                disabled={loading || approvalLoading}
                fullWidth={isMobile}
                sx={{ borderRadius: 2, fontWeight: 800, justifyContent: 'center' }}
              >
                ابدأ من جديد
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => onNavigate(backPath)}
                fullWidth={isMobile}
                sx={{ borderRadius: 2, fontWeight: 800, justifyContent: 'center' }}
              >
                الرجوع لاختيار مشروب من المنيو
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(300px, 0.6fr)' },
            gap: { xs: isActiveMobileConversation ? 0 : 1.25, md: 2.5 },
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              minHeight: 'auto',
              p: { xs: isActiveMobileConversation ? 0.75 : 1.25, sm: 2, md: 3 },
              borderRadius: { xs: isActiveMobileConversation ? 2 : 2.5, md: 4 },
              border: '1px solid rgba(47, 87, 65, 0.14)',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.92), rgba(250,255,236,0.86))',
              boxShadow: '0 24px 70px rgba(23, 53, 37, 0.10)',
              overflow: 'hidden',
              display: 'block',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: suggestions.length
                    ? 'minmax(170px, 0.72fr) minmax(230px, auto) minmax(170px, 0.72fr)'
                    : '1fr',
                },
                alignItems: 'center',
                gap: { xs: 0.75, md: 1.5 },
                textAlign: 'center',
                flexShrink: 0,
                mb: { xs: isActiveMobileConversation ? 0.75 : 0, md: 0 },
              }}
            >
              <Box sx={{ display: { xs: 'none', md: leftOptions.length ? 'block' : 'none' } }}>
                <QuickAnswers
                  suggestions={leftOptions}
                  disabled={loading}
                  onSelect={handleQuickAnswerSelect}
                  selectedValue={selectedOption?.value || ''}
                  columns={{ xs: '1fr' }}
                />
              </Box>

              <Box sx={{ display: 'grid', placeItems: 'center' }}>
                <NuvvyCharacter
                  state={characterState}
                  size={isActiveMobileConversation ? 'small' : isMobile ? 'medium' : 'large'}
                  showBubble
                  message={characterMessage}
                  compact={isActiveMobileConversation}
                />
                {optionNote ? (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.75,
                      px: 1.25,
                      py: 0.65,
                      borderRadius: 999,
                      color: '#2f5741',
                      fontWeight: 800,
                      background: 'rgba(234, 247, 181, 0.62)',
                      border: '1px solid rgba(47, 87, 65, 0.14)',
                      maxWidth: 430,
                    }}
                  >
                    {optionNote}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ display: { xs: 'none', md: rightOptions.length ? 'block' : 'none' } }}>
                <QuickAnswers
                  suggestions={rightOptions}
                  disabled={loading}
                  onSelect={handleQuickAnswerSelect}
                  selectedValue={selectedOption?.value || ''}
                  columns={{ xs: '1fr' }}
                />
              </Box>
            </Box>

            {!conversationStarted && !loading && !finalDrink ? (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => sendMessage('')}
                  fullWidth={isMobile}
                  sx={{ minWidth: { xs: 0, sm: 180 }, borderRadius: 2.5, fontWeight: 950 }}
                >
                  ابدأ
                </Button>
              </Box>
            ) : null}

            {conversationStarted && !finalDrink ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    maxHeight: { xs: isActiveMobileConversation ? 112 : 'none', md: 'none' },
                    overflowY: { xs: isActiveMobileConversation ? 'auto' : 'visible', md: 'visible' },
                  }}
                >
                  <QuickAnswers
                    suggestions={suggestions}
                    disabled={loading}
                    onSelect={handleQuickAnswerSelect}
                    compact={isActiveMobileConversation}
                    selectedValue={selectedOption?.value || ''}
                    columns={{ xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }}
                  />
                </Box>
                <DrinkMessageInput
                  value={message}
                  disabled={loading || !conversationStarted || Boolean(finalDrink)}
                  loading={loading}
                  inputRef={messageInputRef}
                  onChange={handleMessageChange}
                  onSubmit={handleSubmit}
                  compact={isActiveMobileConversation}
                />
              </Box>
            ) : null}

            <FinalDrinkCard
              drink={finalDrink}
              price={finalDrinkPrice}
              drinkId={finalDrinkId}
              approvalLoading={approvalLoading}
              approvalDecision={approvalDecision}
              rateLoading={rateLoading}
              enableRating={enableRating}
              onApprove={() => handleApproveDecision(1)}
              onReject={() => handleApproveDecision(0)}
              onDetails={() => setFinalDrinkModalOpen(true)}
              onRate={() => setRateDialogOpen(true)}
            />
          </Paper>

          <Box sx={{ display: { xs: isActiveMobileConversation ? 'none' : 'block', md: 'block' } }}>
            <ConversationHistory history={conversationHistory} endRef={conversationEndRef} />
          </Box>
        </Box>

        <Dialog
          open={finalDrinkModalOpen && !!finalDrink}
          onClose={() => setFinalDrinkModalOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalCafeIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 950 }}>
              المشروب النهائي المقترح
            </Typography>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              background: 'linear-gradient(180deg, #fff 0%, #fbfff1 100%)',
            }}
          >
            {finalDrink && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1.25} sx={{ mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 950, color: 'warning.dark' }}>
                    {finalDrink.drink_name || finalDrink.name || 'مشروب NUVVY'}
                  </Typography>
                  {Number.isFinite(finalDrinkPrice) && (
                    <Chip label={`سعر المشروب: ${finalDrinkPrice.toFixed(2)} ج.م`} color="warning" sx={{ fontWeight: 900 }} />
                  )}
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {finalDrink.temperature && (
                    <Chip label={`الحرارة: ${finalDrink.temperature}`} color="error" variant="outlined" />
                  )}
                  {finalDrink.taste && (
                    <Chip label={`الطعم: ${finalDrink.taste}`} color="success" variant="outlined" />
                  )}
                </Stack>

                {finalDrink.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                    {finalDrink.description}
                  </Typography>
                ) : null}

                {finalDessert ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: '1px solid rgba(47, 87, 65, 0.14)',
                      background: '#fff',
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={1.25}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#173525', mb: 0.5 }}>
                          الإضافة المختارة
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={getCategoryLabel(finalDessert.category)}
                            color="success"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 800 }}
                          />
                          <Typography sx={{ fontWeight: 900 }}>
                            {finalDessert.name || finalDessert.label}
                          </Typography>
                        </Stack>
                      </Box>
                      {Number.isFinite(finalDessertPrice) ? (
                        <Chip
                          label={`${finalDessertPrice.toFixed(2)} ج.م`}
                          color="warning"
                          sx={{ fontWeight: 900, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                        />
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    color: '#173525',
                    background: 'linear-gradient(135deg, rgba(234, 247, 181, 0.72), rgba(255,255,255,0.96))',
                    border: '1px solid rgba(47, 87, 65, 0.16)',
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1}>
                    <Typography sx={{ fontWeight: 950 }}>
                      الإجمالي
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 950, color: 'success.dark' }}>
                      {finalTotalPrice.toFixed(2)} ج.م
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApproveDecision(1)}
                disabled={approvalLoading || approvalDecision === 1 || !finalDrinkId}
                fullWidth={isMobile}
              >
                قبول المشروب
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleApproveDecision(0)}
                disabled={approvalLoading || approvalDecision === 0 || !finalDrinkId}
                fullWidth={isMobile}
              >
                جرب حاجة تانية
              </Button>
            </Stack>
            <Button fullWidth={isMobile} onClick={() => setFinalDrinkModalOpen(false)}>إغلاق</Button>
          </DialogActions>
        </Dialog>

        {enableRating && (
          <Dialog open={rateDialogOpen} onClose={() => !rateLoading && setRateDialogOpen(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle>
              تقييم المشروب: {finalDrink?.drink_name || finalDrink?.name || '-'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 900 }}>
                    اختر التقييم
                  </Typography>
                  <Rating
                    value={rateValue}
                    onChange={(event, newValue) => setRateValue(newValue || 1)}
                    max={5}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="تعليق"
                  value={rateComment}
                  onChange={(event) => setRateComment(event.target.value)}
                  multiline
                  rows={3}
                  placeholder="اكتب رأيك في المشروب"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRateDialogOpen(false)} disabled={rateLoading}>
                إلغاء
              </Button>
              <Button variant="contained" onClick={handleSubmitRate} disabled={rateLoading}>
                {rateLoading ? 'جاري الإرسال...' : 'إرسال التقييم'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog
          open={dessertModalOpen && isDessertSelection}
          onClose={() => setDessertModalOpen(false)}
          fullScreen={isMobile}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>
                  اختار إضافة مع مشروبك
                </Typography>
                {parsedAgentResponse.message ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.8 }}>
                    {parsedAgentResponse.message}
                  </Typography>
                ) : null}
              </Box>
              <Chip label={`${dessertOptions.length} اختيار`} color="warning" sx={{ fontWeight: 900 }} />
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ background: 'linear-gradient(180deg, #fff 0%, #fbfff1 100%)' }}>
            <Stack spacing={2}>
              {Object.entries(dessertsByCategory).map(([category, items]) => (
                <Box key={category}>
                  <Chip
                    label={getCategoryLabel(category)}
                    color="success"
                    variant="outlined"
                    sx={{ mb: 1.25, fontWeight: 900 }}
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.25,
                    }}
                  >
                    {items.map((item) => {
                      const itemName = item.name || item.label;
                      const price = Number(item.price);
                      const imageUrl = resolveDessertImage(item.image);

                      return (
                        <ButtonBase
                          key={`${category}-${item.value}`}
                          onClick={() => handleDessertSelect(item)}
                          disabled={loading}
                          sx={{ width: '100%', textAlign: 'right', borderRadius: 2.5 }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              width: '100%',
                              p: 1.4,
                              borderRadius: 2.5,
                              border: '1px solid rgba(47, 87, 65, 0.14)',
                              background: '#fff',
                              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                borderColor: 'success.main',
                                boxShadow: '0 14px 28px rgba(23, 53, 37, 0.12)',
                              },
                            }}
                          >
                            <Stack direction="row" alignItems="center" gap={1.25}>
                              {imageUrl ? (
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt={itemName}
                                  loading="lazy"
                                  sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 2,
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    border: '1px solid rgba(47, 87, 65, 0.12)',
                                    background: '#f7faf2',
                                  }}
                                />
                              ) : null}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.25}>
                                  <Typography sx={{ fontWeight: 950, color: '#173525' }}>
                                    {itemName}
                                  </Typography>
                                  {Number.isFinite(price) ? (
                                    <Chip
                                      label={`${price.toFixed(2)} ج.م`}
                                      color="warning"
                                      size="small"
                                      sx={{ fontWeight: 900, flexShrink: 0 }}
                                    />
                                  ) : null}
                                </Stack>
                              </Box>
                            </Stack>
                          </Paper>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDessertModalOpen(false)} fullWidth={isMobile}>
              تخطي الإضافة
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default NuvvyDrinkExperience;
